import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "hr-all-schedule-modal__header" }
const _hoisted_2 = { class: "header-left" }
const _hoisted_3 = { class: "header-actions" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "hr-all-schedule-modal__toolbar" }
const _hoisted_6 = { class: "search-box" }
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "hr-all-schedule-modal__body" }
const _hoisted_9 = {
  key: 0,
  class: "state-box"
}
const _hoisted_10 = {
  key: 1,
  class: "state-box"
}
const _hoisted_11 = {
  key: 2,
  class: "table-wrap"
}
const _hoisted_12 = { class: "schedule-table" }
const _hoisted_13 = { class: "td-name" }
const _hoisted_14 = { class: "td-fee" }
const _hoisted_15 = { class: "td-action" }
const _hoisted_16 = ["onClick"]

import { ref, computed, watch } from 'vue';
import { homeScheduleApi } from '@/services/api';
import { mapSchedulePlanRows, normalizeScheduleDate } from '@/utils/scheduleDate';
import { downloadPdfBlob, getApiErrorMessage } from '@/utils/downloadPdf';
import LtcsMissingRequestCta from '@/components/member/request/LtcsMissingRequestCta.vue';

export interface AllScheduleRecipient {
  patient_code: string;
  patient_name: string;
}

interface ScheduleRow {
  clsp_idx: number;
  clsp_date: string;
  clsp_start_time?: string;
  clsp_end_time?: string;
  clsp_recipient_name: string;
  clsp_recipient_cert_number: string;
  clsp_caregiver_name?: string;
  clsp_service_type?: string;
  clsp_fee_code?: string;
  clsp_fee_name?: string;
}

interface Props {
  modelValue: boolean;
  memberCode: string;
  bizGubun?: string;
  year: number;
  month: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BeneficiaryAllScheduleModal',
  props: {
    modelValue: { type: Boolean },
    memberCode: {},
    bizGubun: {},
    year: {},
    month: {}
  },
  emits: ["update:modelValue", "open-recipient"],
  setup(__props: any, { emit: __emit }) {

const SCHEDULE_LTCS_REQUEST_TYPES = ['caion_ltcs_schedule_plan'] as const;

const props = __props;
const emit = __emit;

const isLoading = ref(false);
const isPdfLoading = ref(false);
const searchText = ref('');
const recipientFilter = ref('');
const scheduleRows = ref<ScheduleRow[]>([]);

const selectedYear = computed(() => props.year);
const selectedMonth = computed(() => props.month);

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const yearMonth = computed(
  () => `${props.year}${String(props.month).padStart(2, '0')}`
);

const stats = computed(() => {
  const codes = new Set(
    scheduleRows.value
      .map((r) => (r.clsp_recipient_cert_number || '').trim())
      .filter(Boolean)
  );
  return {
    rowCount: scheduleRows.value.length,
    recipientCount: codes.size,
  };
});

const recipientOptions = computed(() => {
  const map = new Map<string, { name: string; code: string; count: number }>();
  scheduleRows.value.forEach((row) => {
    const code = (row.clsp_recipient_cert_number || '').trim();
    if (!code) return;
    const cur = map.get(code);
    if (cur) {
      cur.count += 1;
    } else {
      map.set(code, {
        code,
        name: row.clsp_recipient_name || code,
        count: 1,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
});

const filteredRows = computed(() => {
  let rows = scheduleRows.value;
  if (recipientFilter.value) {
    rows = rows.filter(
      (r) => (r.clsp_recipient_cert_number || '').trim() === recipientFilter.value
    );
  }
  const q = searchText.value.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    const hay = [
      r.clsp_recipient_name,
      r.clsp_recipient_cert_number,
      r.clsp_caregiver_name,
      r.clsp_service_type,
      r.clsp_fee_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
});

const formatDate = (val: unknown) => {
  const d = normalizeScheduleDate(val);
  if (!d) return '-';
  const [, m, day] = d.split('-');
  return `${parseInt(m, 10)}/${parseInt(day, 10)}`;
};

const formatTime = (time: string | undefined) => {
  if (!time) return '';
  const str = String(time);
  if (str.includes(':')) return str.substring(0, 5);
  const digits = str.replace(/\D/g, '');
  if (digits.length >= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  return str;
};

const formatTimeRange = (row: ScheduleRow) => {
  const s = formatTime(row.clsp_start_time);
  const e = formatTime(row.clsp_end_time);
  if (s && e) return `${s}-${e}`;
  return s || e || '-';
};

const fetchSchedules = async () => {
  if (!props.memberCode) return;
  isLoading.value = true;
  try {
    let res = await homeScheduleApi.getAllBeneficiarySchedules(
      props.memberCode,
      yearMonth.value,
      props.bizGubun || undefined
    );
    if (
      res.data.success &&
      (!res.data.data || res.data.data.length === 0) &&
      props.bizGubun
    ) {
      res = await homeScheduleApi.getAllBeneficiarySchedules(
        props.memberCode,
        yearMonth.value,
        undefined
      );
    }
    scheduleRows.value = res.data.success ? mapSchedulePlanRows(res.data.data || []) : [];
  } catch (error: unknown) {
    const message = await getApiErrorMessage(error, '전체 일정 조회에 실패했습니다.');
    console.error('전체 일정 조회 오류:', error);
    alert(`전체 일정 조회 실패: ${message}`);
    scheduleRows.value = [];
  } finally {
    isLoading.value = false;
  }
};

const downloadAllPdf = async () => {
  if (!props.memberCode || stats.value.rowCount === 0) return;
  if (
    !confirm(
      `${selectedYear.value}년 ${selectedMonth.value}월 수급자 ${stats.value.recipientCount}명의 일정표를 PDF로 출력하시겠습니까?`
    )
  ) {
    return;
  }
  isPdfLoading.value = true;
  try {
    let res = await homeScheduleApi.downloadAllBeneficiariesPdf(
      props.memberCode,
      yearMonth.value,
      props.bizGubun || undefined
    );
    if (res.status === 404 && props.bizGubun) {
      res = await homeScheduleApi.downloadAllBeneficiariesPdf(
        props.memberCode,
        yearMonth.value,
        undefined
      );
    }
    downloadPdfBlob(res.data, `전체수급자_일정표_${yearMonth.value}.pdf`);
  } catch (error: unknown) {
    const message = await getApiErrorMessage(error, '전체 PDF 출력에 실패했습니다.');
    console.error('전체 PDF 오류:', error);
    alert(`전체 PDF 출력 실패: ${message}`);
  } finally {
    isPdfLoading.value = false;
  }
};

const openRecipientCalendar = (row: ScheduleRow) => {
  const code = (row.clsp_recipient_cert_number || '').trim();
  if (!code) return;
  emit('open-recipient', {
    patient_code: code,
    patient_name: row.clsp_recipient_name || code,
  });
};

const close = () => {
  dialogVisible.value = false;
};

watch(dialogVisible, (visible) => {
  if (visible) {
    searchText.value = '';
    recipientFilter.value = '';
    fetchSchedules();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: dialogVisible.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialogVisible).value = $event)),
    "max-width": "1280px",
    scrollable: "",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "hr-all-schedule-modal" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_v_icon, {
                size: "24",
                color: "white"
              }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode("mdi-calendar-multiple", -1)
                ]))]),
                _: 1
              }),
              _createElementVNode("div", null, [
                _cache[5] || (_cache[5] = _createElementVNode("h3", null, "수급자 전체 일정계획", -1)),
                _createElementVNode("p", null, _toDisplayString(selectedYear.value) + "년 " + _toDisplayString(selectedMonth.value) + "월 · " + _toDisplayString(stats.value.recipientCount) + "명 · " + _toDisplayString(stats.value.rowCount) + "건", 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("button", {
                type: "button",
                class: "pdf-btn",
                disabled: isPdfLoading.value || stats.value.rowCount === 0,
                onClick: downloadAllPdf
              }, [
                _createVNode(_component_v_icon, {
                  size: "18",
                  color: "white"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(isPdfLoading.value ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box'), 1)
                  ]),
                  _: 1
                }),
                _cache[6] || (_cache[6] = _createElementVNode("span", null, "전체 PDF", -1))
              ], 8, _hoisted_4),
              _createElementVNode("button", {
                type: "button",
                class: "close-btn",
                onClick: close
              }, [
                _createVNode(_component_v_icon, {
                  size: "20",
                  color: "white"
                }, {
                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode("mdi-close", -1)
                  ]))]),
                  _: 1
                })
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_v_icon, {
                size: "16",
                color: "#64748b"
              }, {
                default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                  _createTextVNode("mdi-magnify", -1)
                ]))]),
                _: 1
              }),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
                type: "text",
                placeholder: "수급자명, 인정번호, 요양보호사 검색..."
              }, null, 512), [
                [_vModelText, searchText.value]
              ]),
              (searchText.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "search-clear",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (searchText.value = ''))
                  }, [
                    _createVNode(_component_v_icon, { size: "12" }, {
                      default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                        _createTextVNode("mdi-close", -1)
                      ]))]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true)
            ]),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((recipientFilter).value = $event)),
              class: "recipient-filter"
            }, [
              _cache[10] || (_cache[10] = _createElementVNode("option", { value: "" }, "전체 수급자", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recipientOptions.value, (r) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: r.code,
                  value: r.code
                }, _toDisplayString(r.name) + " (" + _toDisplayString(r.count) + "건) ", 9, _hoisted_7))
              }), 128))
            ], 512), [
              [_vModelSelect, recipientFilter.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(_component_v_progress_circular, {
                    indeterminate: "",
                    color: "#1e3a5f",
                    size: "40"
                  }),
                  _cache[11] || (_cache[11] = _createElementVNode("span", null, "일정을 불러오는 중...", -1))
                ]))
              : (filteredRows.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createVNode(_component_v_icon, {
                      size: "48",
                      color: "#cbd5e1"
                    }, {
                      default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                        _createTextVNode("mdi-calendar-blank", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[13] || (_cache[13] = _createElementVNode("p", null, "해당 연월에 표시할 일정이 없습니다.", -1)),
                    _createVNode(LtcsMissingRequestCta, {
                      "member-code": __props.memberCode,
                      "biz-gubun": __props.bizGubun,
                      "year-month": yearMonth.value,
                      types: SCHEDULE_LTCS_REQUEST_TYPES,
                      onUpdated: fetchSchedules
                    }, null, 8, ["member-code", "biz-gubun", "year-month"])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("table", _hoisted_12, [
                      _cache[15] || (_cache[15] = _createElementVNode("thead", null, [
                        _createElementVNode("tr", null, [
                          _createElementVNode("th", null, "No"),
                          _createElementVNode("th", null, "일자"),
                          _createElementVNode("th", null, "시간"),
                          _createElementVNode("th", null, "수급자"),
                          _createElementVNode("th", null, "인정번호"),
                          _createElementVNode("th", null, "요양보호사"),
                          _createElementVNode("th", null, "서비스"),
                          _createElementVNode("th", null, "수가"),
                          _createElementVNode("th")
                        ])
                      ], -1)),
                      _createElementVNode("tbody", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredRows.value, (row, idx) => {
                          return (_openBlock(), _createElementBlock("tr", {
                            key: row.clsp_idx
                          }, [
                            _createElementVNode("td", null, _toDisplayString(idx + 1), 1),
                            _createElementVNode("td", null, _toDisplayString(formatDate(row.clsp_date)), 1),
                            _createElementVNode("td", null, _toDisplayString(formatTimeRange(row)), 1),
                            _createElementVNode("td", _hoisted_13, _toDisplayString(row.clsp_recipient_name), 1),
                            _createElementVNode("td", null, _toDisplayString(row.clsp_recipient_cert_number || '-'), 1),
                            _createElementVNode("td", null, _toDisplayString(row.clsp_caregiver_name || '-'), 1),
                            _createElementVNode("td", null, _toDisplayString(row.clsp_service_type || '-'), 1),
                            _createElementVNode("td", _hoisted_14, _toDisplayString(row.clsp_fee_name || row.clsp_fee_code || '-'), 1),
                            _createElementVNode("td", _hoisted_15, [
                              _createElementVNode("button", {
                                type: "button",
                                class: "row-calendar-btn",
                                title: "이 수급자 달력 보기",
                                onClick: ($event: any) => (openRecipientCalendar(row))
                              }, [
                                _createVNode(_component_v_icon, { size: "16" }, {
                                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                    _createTextVNode("mdi-calendar-clock", -1)
                                  ]))]),
                                  _: 1
                                })
                              ], 8, _hoisted_16)
                            ])
                          ]))
                        }), 128))
                      ])
                    ])
                  ]))
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})